import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { invitationsAPI } from '../../services/api';

const AcceptInvite = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const oauthError = searchParams.get('error');
    const navigate = useNavigate();

    // Accept the invite with Google: send the invitation token through OAuth so
    // the callback links this Google account to the invited workspace (with the
    // invited role) instead of spinning up a brand-new workspace.
    const handleGoogleAccept = () => {
        const returnUrl = encodeURIComponent(`${window.location.origin}/`);
        const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
        window.location.href = `${base}/users/auth/google_oauth2?return_to=${returnUrl}&invitation_token=${encodeURIComponent(token)}`;
    };

    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [errorTitle, setErrorTitle] = useState('Invitation invalid');
    const [success, setSuccess] = useState('');

    // Form fields for new users
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setErrorTitle('Invalid invitation link');
            setError('This link is missing its invitation token. Ask the inviter to resend it.');
            setLoading(false);
            return;
        }

        invitationsAPI.getByToken(token)
            .then(res => {
                setInvitation(res.data);
                setLoading(false);
            })
            .catch(err => {
                const status = err.response?.status;
                const apiError = err.response?.data?.error;
                // Show the REAL reason, not a blanket "expired". A 404 (wrong/stale
                // token) is not the same as a genuinely expired invite, and
                // mislabeling it sends people chasing the wrong problem.
                if (status === 404) {
                    setErrorTitle('Invitation not found');
                    setError(apiError || "We couldn't find this invitation. The link may be incomplete or the invite was cancelled.");
                } else if (/expired/i.test(apiError || '')) {
                    setErrorTitle('Invitation expired');
                    setError(apiError);
                } else if (status === 410) {
                    setErrorTitle('Invitation no longer valid');
                    setError(apiError || 'This invitation has already been used or cancelled.');
                } else {
                    setErrorTitle('Invitation invalid');
                    setError(apiError || 'This invitation is no longer valid.');
                }
                setLoading(false);
            });
    }, [token]);

    const handleAccept = async (e) => {
        e.preventDefault();
        if (password !== passwordConfirmation) {
            setError('Passwords do not match');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const response = await invitationsAPI.accept(token, { name, password, password_confirmation: passwordConfirmation });
            setSuccess(response?.status?.message || 'Account created! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.status?.message || 'Failed to accept invitation.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/50">
                <div className="text-muted-foreground text-sm">Loading invitation...</div>
            </div>
        );
    }

    if (error && !invitation) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/50 px-4">
                <div className="w-full max-w-md text-center">
                    <div className="bg-card rounded-xl border border-border p-4 shadow-sm">
                        <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-foreground mb-2">{errorTitle}</h2>
                        <p className="text-muted-foreground text-sm mb-6">{error}</p>
                        <Link to="/login" className="text-sm font-medium text-foreground hover:underline">
                            Go to login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/50 px-4">
            <div className="w-full max-w-md">
                <div className="bg-card rounded-xl border border-border p-4 shadow-sm">
                    <h2 className="text-xl font-semibold text-foreground mb-1">Join the team</h2>
                    <p className="text-muted-foreground text-sm mb-2">
                        <strong className="text-foreground">{invitation?.inviter_name}</strong> invited you as
                        {invitation?.role === 'admin' ? ' an ' : ' a '}
                        <span className="font-medium text-indigo-600 capitalize">{invitation?.role}</span>.
                    </p>

                    {invitation?.clients?.length > 0 && (
                        <div className="bg-muted/50 rounded-lg p-3 mb-6">
                            <p className="text-xs font-medium text-muted-foreground mb-1.5">Workspace access:</p>
                            {invitation.clients.map(c => (
                                <span key={c.id} className="inline-flex items-center mr-2 mb-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {c.name}
                                </span>
                            ))}
                        </div>
                    )}

                    {oauthError === 'seat_limit' && (
                        <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600 mb-4">
                            This workspace has reached its member limit. Ask the owner to free a seat or upgrade, then try again.
                        </div>
                    )}

                    {/* Accept via Google — joins the invited workspace with this Google account */}
                    <button
                        type="button"
                        onClick={handleGoogleAccept}
                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-4 h-4" />
                        Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-5">
                        <div className="h-px bg-border flex-1" />
                        <span className="text-xs text-muted-foreground">or set a password</span>
                        <div className="h-px bg-border flex-1" />
                    </div>

                    <form onSubmit={handleAccept} className="space-y-4">
                        {success && (
                            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                                {success}
                            </div>
                        )}
                        {error && (
                            <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                            <input
                                type="email"
                                value={invitation?.email || ''}
                                disabled
                                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-muted-foreground bg-muted/50"
                            />
                        </div>
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">Full name</label>
                            <input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                placeholder="Your full name"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                            <input
                                id="password"
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                placeholder="Min. 6 characters"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                minLength={6}
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                placeholder="Repeat password"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || success}
                            className="w-full py-2.5 px-4 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Creating account...' : 'Accept & create account'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvite;
